import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { AudioPlayerContext, type AudioPlayerSource } from "./context.js";

export interface AudioPlayerProviderProps {
  window?: Window;
  children?: ReactNode;
}

interface AudioPlayerRequest {
  id: number;
  sourceKey: string | undefined;
  sourceSrc: string | undefined;
  seekTime: number | undefined;
  shouldPlay: boolean;
  hasRequestedPosition: boolean;
}

/** Facilitates communication between the subject/source of some audio (such as the transcript) and the audio player (which may render on a separate surface area). */
export const AudioPlayerProvider: FC<AudioPlayerProviderProps> = (props) => {
  const window = props.window ?? globalThis.window;

  const [sources, setSources] = useState<readonly AudioPlayerSource[]>([]);

  const registerSourcesProvider = useCallback(() => {
    let isUnregistered = false;
    let prevSources: readonly AudioPlayerSource[] = [];

    const updateSources = (nextSources: readonly AudioPlayerSource[]) => {
      const addedSources = nextSources.filter(
        (source) => !prevSources.some((prev) => prev.key === source.key),
      );
      const updatedSources = nextSources.filter((source) =>
        prevSources.some((prev) => prev.key === source.key),
      );
      const deletedSources = prevSources.filter(
        (prev) => !nextSources.some((source) => source.key === prev.key),
      );
      prevSources = nextSources;

      setSources((currentSources) => {
        return [
          ...addedSources,
          ...updatedSources,
          ...currentSources.filter(
            (source) =>
              !addedSources.some(
                (addedSource) => addedSource.key === source.key,
              ) &&
              !updatedSources.some(
                (updatedSource) => updatedSource.key === source.key,
              ) &&
              !deletedSources.some(
                (deletedSource) => deletedSource.key === source.key,
              ),
          ),
        ].sort((a, b) => a.sortTimestamp - b.sortTimestamp);
      });
    };

    return {
      onSourcesChange: (newSources: readonly AudioPlayerSource[]) => {
        if (isUnregistered) {
          return;
        }

        updateSources(newSources);
      },
      unregister: () => {
        if (isUnregistered) {
          return;
        }

        isUnregistered = true;
        updateSources([]);
      },
    };
  }, []);

  const [request, setRequest] = useState<AudioPlayerRequest>({
    id: 0,
    sourceKey: undefined,
    sourceSrc: undefined,
    seekTime: undefined,
    shouldPlay: false,
    hasRequestedPosition: false,
  });

  // If no source is selected, or if the selected source is no longer available, automatically select the first source (if any).
  // TODO: Do this all in one cycle using a useReducer
  const defaultSource = sources.at(0);
  const requestSourceKey = request.sourceKey;
  const requestSource = sources.find((s) => s.key === requestSourceKey);
  if (
    (requestSourceKey === undefined || !requestSource) &&
    defaultSource !== undefined
  ) {
    setRequest((prev) => ({
      id: prev.id + 1,
      sourceKey: defaultSource.key,
      sourceSrc: defaultSource.src,
      seekTime: undefined,
      shouldPlay: false,
      hasRequestedPosition: false,
    }));
  }
  // If the source's URL became available, re-create the request.
  else if (
    request.sourceSrc === undefined &&
    requestSource?.src !== undefined
  ) {
    setRequest((prev) =>
      // Recheck the conditions in case the state got updated in the meantime
      prev.sourceKey === requestSource.key && prev.sourceSrc === undefined
        ? { ...prev, id: prev.id + 1, sourceSrc: requestSource.src }
        : prev,
    );
  }
  // If the source's URL was replaced (such as a re-signed URL after the old one expired),
  // pick it up without repeating earlier seek/play requests.
  else if (
    request.sourceSrc !== undefined &&
    requestSource?.src !== undefined &&
    requestSource.src !== request.sourceSrc
  ) {
    setRequest((prev) =>
      prev.sourceKey === requestSource.key &&
      prev.sourceSrc !== undefined &&
      prev.sourceSrc !== requestSource.src
        ? {
            id: prev.id + 1,
            sourceKey: prev.sourceKey,
            sourceSrc: requestSource.src,
            seekTime: undefined,
            shouldPlay: false,
            hasRequestedPosition: prev.hasRequestedPosition,
          }
        : prev,
    );
  }

  const [{ audioContext, nativeAudio }] = useState(() => {
    const nativeAudio = window.document.createElement("audio");

    // CORS is required for the Web Audio API
    nativeAudio.crossOrigin = "anonymous";

    // Set up audio processing, if the Web Audio API is available
    const audioContext =
      typeof globalThis.AudioContext !== "undefined"
        ? new AudioContext()
        : null;

    if (audioContext) {
      const source = audioContext.createMediaElementSource(nativeAudio);

      // Merge any and all channels into one, because some files have each speaker/participant per channel
      const merger = audioContext.createChannelMerger(1);

      // High-pass filter to remove rumble/hum
      const highpass = audioContext.createBiquadFilter();
      highpass.type = "highpass";
      highpass.frequency.setValueAtTime(80, audioContext.currentTime);

      // Low-pass filter to remove high-frequency noise
      const lowpass = audioContext.createBiquadFilter();
      lowpass.type = "lowpass";
      lowpass.frequency.setValueAtTime(3400, audioContext.currentTime);

      // Compressor for speech
      const compressor = audioContext.createDynamicsCompressor();
      compressor.threshold.setValueAtTime(-20, audioContext.currentTime);
      compressor.ratio.setValueAtTime(6, audioContext.currentTime);
      compressor.knee.setValueAtTime(3, audioContext.currentTime);
      compressor.attack.setValueAtTime(0.015, audioContext.currentTime);
      compressor.release.setValueAtTime(0.075, audioContext.currentTime);

      // Output gain to compensate for compression
      const outputGain = audioContext.createGain();
      outputGain.gain.setValueAtTime(1.2, audioContext.currentTime);

      // Connect chain
      source.connect(merger, 0, 0);
      merger.connect(highpass);
      highpass.connect(lowpass);
      lowpass.connect(compressor);
      compressor.connect(outputGain);
      outputGain.connect(audioContext.destination);
    }

    return {
      audioContext,
      nativeAudio,
    };
  });

  useEffect(() => {
    // The audio context may suspend when there is no audio loaded, so we may have to resume it once we attempt to start playback
    const onPlay = () => {
      if (audioContext?.state === "suspended") {
        audioContext.resume();
      }
    };

    nativeAudio.addEventListener("play", onPlay);

    return () => {
      nativeAudio.removeEventListener("play", onPlay);

      nativeAudio.pause();
      delete nativeAudio.dataset["arSourceKey"];
      delete nativeAudio.dataset["arSourceSrc"];
      nativeAudio.removeAttribute("src");
      nativeAudio.load();
    };
  }, [audioContext, nativeAudio]);

  useEffect(() => {
    const isSameSource =
      nativeAudio.dataset["arSourceKey"] === request.sourceKey;
    if (
      isSameSource &&
      nativeAudio.dataset["arSourceSrc"] === request.sourceSrc
    ) {
      return;
    }

    const originalPlaybackRate = nativeAudio.playbackRate;

    // When only the URL changed for the same source, keep the playback position and state
    const resumeTime = isSameSource ? nativeAudio.currentTime : undefined;
    const shouldResume = isSameSource && !nativeAudio.paused;

    const src = request.sourceSrc;
    if (src) {
      nativeAudio.dataset["arSourceKey"] = request.sourceKey;
      nativeAudio.dataset["arSourceSrc"] = src;
      nativeAudio.src = src;
    } else {
      delete nativeAudio.dataset["arSourceKey"];
      delete nativeAudio.dataset["arSourceSrc"];
      nativeAudio.removeAttribute("src");
    }

    nativeAudio.load();

    // Keep the playback rate
    nativeAudio.playbackRate = originalPlaybackRate;

    if (src && resumeTime) {
      nativeAudio.currentTime = resumeTime;
    }
    if (src && shouldResume) {
      nativeAudio.play().catch(() => {
        // Leave it paused if the browser blocks the resume
      });
    }
  }, [nativeAudio, request.sourceKey, request.sourceSrc]);

  const handledSeekRequestIdRef = useRef(0);
  useEffect(() => {
    if (
      handledSeekRequestIdRef.current < request.id &&
      request.seekTime !== undefined
    ) {
      handledSeekRequestIdRef.current = request.id;
      nativeAudio.currentTime = request.seekTime;
    }
  }, [nativeAudio, request]);

  const handledPlayRequestIdRef = useRef(0);
  useEffect(() => {
    if (
      nativeAudio &&
      handledPlayRequestIdRef.current < request.id &&
      request.shouldPlay
    ) {
      handledPlayRequestIdRef.current = request.id;
      nativeAudio.play();
    }
  }, [nativeAudio, request]);

  const play = useEffectEvent((sourceKey: string, time?: number) => {
    setRequest((prev) => ({
      id: prev.id + 1,
      sourceKey,
      sourceSrc: sources.find((s) => s.key === sourceKey)?.src,
      seekTime: time,
      shouldPlay: true,
      hasRequestedPosition: true,
    }));
  });

  const seek = useEffectEvent((sourceKey: string, time?: number) => {
    setRequest((prev) => ({
      id: prev.id + 1,
      sourceKey,
      sourceSrc: sources.find((s) => s.key === sourceKey)?.src,
      seekTime: time,
      shouldPlay: false,
      hasRequestedPosition: true,
    }));
  });

  const currentSourceKey = request.sourceKey;
  const context = useMemo(() => {
    return {
      sources,
      registerSourcesProvider,
      currentSourceKey,
      hasRequestedPosition: request.hasRequestedPosition,
      play,
      seek,
      nativeAudio,
    };
  }, [
    sources,
    registerSourcesProvider,
    currentSourceKey,
    request.hasRequestedPosition,
    play,
    seek,
    nativeAudio,
  ]);

  return (
    <AudioPlayerContext.Provider value={context}>
      {props.children}
    </AudioPlayerContext.Provider>
  );
};
